import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Pending", "Paid", "Packing", "Dispatched", "Delivered", "Cancelled"];
const FILTERS = ["All", ...STATUSES];
const STATUS_STYLES = {
  Pending: "bg-stone-100 text-stone-700",
  Paid: "bg-emerald-50 text-emerald-800",
  Packing: "bg-amber-50 text-amber-800",
  Dispatched: "bg-sky-50 text-sky-800",
  Delivered: "bg-[#C5A059]/15 text-[#9E7B3A]",
  Cancelled: "bg-red-50 text-red-700",
};
const EMPTY_CUSTOMER = { name: "", email: "", phone: "", address: { line1: "", city: "", state: "", postcode: "" } };

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [lines, setLines] = useState([{ product_id: "", qty: 1 }]);
  const [shipping, setShipping] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const updateOrder = async (id, updates) => {
    try {
      const { data } = await api.put(`/admin/orders/${id}`, updates);
      setSelected(data);
      toast.success("Order updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createOrder = async (e) => {
    e.preventDefault();
    const items = lines.filter((l) => l.product_id && l.qty > 0);
    if (!items.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      await api.post("/admin/orders", {
        customer, items, shipping: parseFloat(shipping) || 0,
        payment_method: "manual", status: "Pending",
      });
      toast.success("Order created");
      setCreating(false);
      setCustomer(EMPTY_CUSTOMER);
      setLines([{ product_id: "", qty: 1 }]);
      setShipping(0);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.order_number}?`)) return;
    await api.delete(`/admin/orders/${o.id}`);
    toast.success("Order deleted");
    setSelected(null);
    load();
  };

  const setCust = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));
  const setAddr = (k, v) => setCustomer((c) => ({ ...c, address: { ...c.address, [k]: v } }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const visible = filter === "All" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl font-light mb-2">Orders</h1>
          <p className="text-sm text-[#4A4A4A]">Pack, manage and send every order from here.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="create-order-button"
          className="flex items-center gap-2 bg-[#1A1A1A] text-white text-xs tracking-[0.2em] uppercase px-5 py-3 hover:bg-[#9E7B3A] transition-colors duration-300">
          <Plus className="w-4 h-4" /> New Order
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="order-status-filters">
        {FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
            className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
              filter === s ? "border-[#9E7B3A] text-[#9E7B3A] bg-[#C5A059]/10" : "border-[#C5A059]/30 text-[#4A4A4A] hover:border-[#9E7B3A]"
            }`}>
            {s} {s !== "All" && `(${orders.filter((o) => o.status === s).length})`}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#C5A059]/25 overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead>
            <tr className="border-b border-[#C5A059]/20 text-left">
              {["Order", "Customer", "Items", "Total", "Status", "Date"].map((h) => (
                <th key={h} className="px-5 py-4 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#C5A059]/10">
            {visible.map((o) => (
              <tr key={o.id} onClick={() => setSelected(o)} data-testid={`order-row-${o.id}`}
                className="cursor-pointer hover:bg-[#C5A059]/5 transition-colors duration-200">
                <td className="px-5 py-4 font-medium">{o.order_number}</td>
                <td className="px-5 py-4">{o.customer?.name}</td>
                <td className="px-5 py-4 text-[#4A4A4A]">{o.items?.reduce((n, i) => n + i.qty, 0)}</td>
                <td className="px-5 py-4">${o.total?.toFixed(2)}</td>
                <td className="px-5 py-4">
                  <span className={`text-[10px] tracking-widest uppercase px-2.5 py-1 ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                </td>
                <td className="px-5 py-4 text-[#4A4A4A]">{fmtDate(o.created_at)}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-[#4A4A4A]" data-testid="no-orders-row">No orders in this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Order detail */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-light">{selected.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-2 text-sm">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] mb-2">Customer</p>
                    <p className="font-medium">{selected.customer?.name}</p>
                    <Input type="email" defaultValue={selected.customer?.email || ""} key={selected.id + "email"}
                      placeholder="Customer email (needed to email invoice)" data-testid="order-customer-email-edit"
                      className="rounded-none mt-1.5 h-8 text-xs"
                      onBlur={(e) => e.target.value !== (selected.customer?.email || "") && updateOrder(selected.id, { customer_email: e.target.value })} />
                    <p className="text-[#4A4A4A] mt-1">{selected.customer?.phone}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] mb-2">Ship To</p>
                    <p className="text-[#4A4A4A]">{selected.customer?.address?.line1}</p>
                    <p className="text-[#4A4A4A]">
                      {[selected.customer?.address?.city, selected.customer?.address?.state, selected.customer?.address?.postcode].filter(Boolean).join(" ")}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] mb-2">Packing List</p>
                  <div className="border border-[#C5A059]/25 divide-y divide-[#C5A059]/10" data-testid="order-items-list">
                    {selected.items?.map((i, idx) => (
                      <div key={idx} className="flex justify-between px-4 py-2.5">
                        <span>{i.qty} × {i.name}</span>
                        <span>${(i.qty * i.price).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between px-4 py-2.5 text-[#4A4A4A]"><span>Shipping</span><span>${selected.shipping?.toFixed(2)}</span></div>
                    <div className="flex justify-between px-4 py-2.5 font-medium"><span>Total</span><span>${selected.total?.toFixed(2)} AUD</span></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs tracking-widest uppercase">Status</Label>
                    <Select value={selected.status} onValueChange={(v) => updateOrder(selected.id, { status: v })}>
                      <SelectTrigger data-testid="order-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs tracking-widest uppercase">Tracking Number</Label>
                    <div className="flex gap-2">
                      <Input defaultValue={selected.tracking_number} key={selected.id + selected.tracking_number}
                        data-testid="order-tracking-input" className="rounded-none" placeholder="AUSPOST…"
                        onBlur={(e) => e.target.value !== selected.tracking_number && updateOrder(selected.id, { tracking_number: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs tracking-widest uppercase">Internal Notes</Label>
                  <Textarea defaultValue={selected.notes} key={selected.id + "notes"} rows={2} data-testid="order-notes-input" className="rounded-none"
                    onBlur={(e) => e.target.value !== selected.notes && updateOrder(selected.id, { notes: e.target.value })} />
                </div>
                <div className="flex justify-between items-center pt-2">
                  <p className="text-xs text-[#4A4A4A]">Placed {fmtDate(selected.created_at)} · {selected.payment_method}</p>
                  <div className="flex items-center gap-5">
                    <Link to={`/admin/orders/${selected.id}/invoice`} data-testid="order-invoice-button"
                      className="text-xs tracking-widest uppercase text-[#9E7B3A] hover:text-[#1A1A1A] transition-colors duration-300">
                      View Invoice
                    </Link>
                    <button onClick={() => removeOrder(selected)} data-testid="order-delete-button"
                      className="text-xs tracking-widest uppercase text-red-700 hover:text-red-900 transition-colors duration-300">
                      Delete Order
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create order */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="create-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">New Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={createOrder} className="space-y-4 mt-2 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs tracking-widest uppercase">Customer Name</Label>
                <Input required value={customer.name} onChange={(e) => setCust("name", e.target.value)} data-testid="order-customer-name" className="rounded-none" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Email</Label>
                <Input type="email" value={customer.email} onChange={(e) => setCust("email", e.target.value)} data-testid="order-customer-email" className="rounded-none" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Phone</Label>
                <Input value={customer.phone} onChange={(e) => setCust("phone", e.target.value)} data-testid="order-customer-phone" className="rounded-none" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs tracking-widest uppercase">Address</Label>
                <Input value={customer.address.line1} onChange={(e) => setAddr("line1", e.target.value)} data-testid="order-address-line1" className="rounded-none" placeholder="Street" />
              </div>
              <Input value={customer.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="order-address-city" className="rounded-none" placeholder="City" />
              <div className="grid grid-cols-2 gap-4">
                <Input value={customer.address.state} onChange={(e) => setAddr("state", e.target.value)} data-testid="order-address-state" className="rounded-none" placeholder="State" />
                <Input value={customer.address.postcode} onChange={(e) => setAddr("postcode", e.target.value)} data-testid="order-address-postcode" className="rounded-none" placeholder="Postcode" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-widest uppercase">Items</Label>
              {lines.map((l, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <Select value={l.product_id} onValueChange={(v) => setLine(i, "product_id", v)}>
                    <SelectTrigger data-testid={`order-line-product-${i}`} className="rounded-none flex-1"><SelectValue placeholder="Choose product" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.price.toFixed(2)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="1" value={l.qty} onChange={(e) => setLine(i, "qty", parseInt(e.target.value) || 1)}
                    data-testid={`order-line-qty-${i}`} className="rounded-none w-20" />
                  {lines.length > 1 && (
                    <button type="button" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label="Remove item">
                      <Trash2 className="w-4 h-4 text-[#4A4A4A]" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setLines((ls) => [...ls, { product_id: "", qty: 1 }])} data-testid="order-add-line"
                className="text-xs tracking-widest uppercase text-[#9E7B3A] hover:text-[#1A1A1A] transition-colors duration-300">
                + Add Item
              </button>
            </div>
            <div className="space-y-1.5 w-40">
              <Label className="text-xs tracking-widest uppercase">Shipping ($)</Label>
              <Input type="number" step="0.01" min="0" value={shipping} onChange={(e) => setShipping(e.target.value)} data-testid="order-shipping-input" className="rounded-none" />
            </div>
            <button type="submit" disabled={saving} data-testid="order-create-submit"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
              {saving ? "Creating…" : "Create Order"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
